import React, { useEffect } from 'react';
import clsx from 'clsx';
import _ from 'underscore';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@mui/styles';
import { Button, Card, CardHeader, CardContent, CardActions, Grid, Divider, TextField, colors, gridClasses } from '@mui/material';
import DatePicker from 'components/DatePicker/DatePicker';
import validate from 'validate.js';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { enqueueSnackbar } from 'actions';
import { ReportSmallCellsImpactExport, SetFormState } from '../../actions/ReportSmallCellsImpactActions';

const useStyles = makeStyles((theme) => ({
	root: {
		padding: theme.spacing(3),
	},
	container: {
		marginTop: theme.spacing(3),
	},
	tabs: {
		marginTop: theme.spacing(3),
	},
	content: {
		marginTop: theme.spacing(3),
	},
	generate: {
		color: theme.palette.white,
		backgroundColor: colors.green[600],
		'&:hover': {
			backgroundColor: colors.green[900],
		},
	},
}));

const schema = {
	macroCells: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	smallCells: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	pre: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	post: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	activationDate: {
		presence: { allowEmpty: false, message: 'is required' },
	}
};

const ReportSmallCellsImpactForm = () => {
	const classes = useStyles();
	const dispatch = useDispatch();
	const setFormState = formState => dispatch(SetFormState(formState));
	const formState = useSelector((state) => state.ReportSmallCellsImpactReducer.formState);

	useEffect(() => {
		const errors = validate(formState.values, schema);

		setFormState({
			...formState,
			isValid: !errors,
			errors: errors || {},
		});
	}, [formState.values]);

	const _handleChange = (event) => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				[event.target.name]:
					event.target.type === 'checkbox'
						? event.target.checked
						: event.target.value,
			},
			touched: {
				...formState.touched,
				[event.target.name]: true,
			},
		});
	};

	const _handleDateChange = (value, name) => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				[name]: value,
			},
			touched: {
				...formState.touched,
				[name]: true,
			},
		});
	};

	const _hasError = (field) => (!!(formState.touched[field] && formState.errors[field]));
	const user = useSelector(state => state.session.user);
	const _generateImpactReport = () => {
		dispatch(ReportSmallCellsImpactExport(formState.values, user.id));
	};

	return (
		<Card className={classes.content}>
			<CardHeader title="Report Impact" />
			<Divider />
			<CardContent>
				<Grid container spacing={1}>
					<Grid item md={3} xs={12}>
						<TextField
							fullWidth
							label="Pre"
							name="pre"
							onChange={_handleChange}
							value={formState.values.pre}
							variant="outlined"
							error={_hasError('pre')}
							helperText={
								_hasError('pre') ? formState.errors.pre[0] : 'Example: 10'
							}
						/>
					</Grid>
					<Grid item md={3} xs={12}>
						<TextField
							fullWidth
							label="Post"
							name="post"
							onChange={_handleChange}
							value={formState.values.post}
							variant="outlined"
							error={_hasError('post')}
							helperText={
								_hasError('post') ? formState.errors.post[0] : 'Example: 10'
							}
						/>
					</Grid>
					<Grid item md={6} xs={12}>
						<DatePicker
							label="Activation Date"
							name="activationDate"
							value={formState.values.activationDate || null}
							onChange={(value) => _handleDateChange(value, 'activationDate')}
							error={_hasError('activationDate')}
							helperText={_hasError('activationDate') ? formState.errors.activationDate[0] : null}
						/>
					</Grid>
				</Grid>
				<br />
				<Grid container spacing={1}>
					<Grid item md={6} xs={12}>
						<TextField
							fullWidth
							label="Macro Cells List"
							name="macroCells"
							onChange={_handleChange}
							value={formState.values.macroCells}
							variant="outlined"
							error={_hasError('macroCells')}
							helperText={
								_hasError('macroCells') ? formState.errors.macroCells[0] : 'Example: 90048A11,90048A18,90048A21,90048A26'
							}
						/>
					</Grid>
					<Grid item md={6} xs={12}>
						<TextField
							fullWidth
							label="Small Cells List"
							name="smallCells"
							onChange={_handleChange}
							value={formState.values.smallCells}
							variant="outlined"
							error={_hasError('smallCells')}
							helperText={
								_hasError('smallCells') ? formState.errors.smallCells[0] : 'Example: 32018A19,32018A24,32018A28'
							}
						/>
					</Grid>
				</Grid>
			</CardContent>
			<Divider />
			<CardActions>
				<Button className={classes.generate} onClick={_generateImpactReport} variant="contained" disabled={!formState.isValid}>Export</Button>
			</CardActions>
		</Card>
	);
};

export default ReportSmallCellsImpactForm;
